import React, { useEffect, useState } from "react";
import {
  Box,
  Paper,
  Typography,
  LinearProgress,
  IconButton,
  Button,
  Chip,
  Alert,
  Collapse,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Divider,
} from "@mui/material";
import {
  Cancel,
  CheckCircle,
  Error,
  HourglassEmpty,
  PlayArrow,
  Refresh,
  ExpandMore,
  ExpandLess,
} from "@mui/icons-material";
import axios from "axios";
import { hostname } from "../../utils";

interface TaskResult {
  // CSV import format
  totalRows?: number;
  successCount?: number;
  failedCount?: number;
  createdCount?: number;
  updatedCount?: number;
  // People search format
  totalFound?: number;
  imported?: number;
  updated?: number;
  failed?: number;
}

interface BackgroundTask {
  id: string;
  taskType: string;
  status: string;
  totalItems: number;
  processedItems: number;
  successItems: number;
  failedItems: number;
  progressMessage: string;
  errorMessage: string;
  progressPercentage: number;
  createdDate: string;
  startedDate: string;
  completedDate: string;
  taskMetadata: any;
  result?: TaskResult;
}

interface BackgroundTasksPanelProps {
  listId: string;
  companyId: string;
  getToken: () => Promise<string>;
  onTaskComplete?: () => void;
  refreshTrigger?: number; // Add this prop to trigger refresh
}

const BackgroundTasksPanel: React.FC<BackgroundTasksPanelProps> = ({
  listId,
  companyId,
  getToken,
  onTaskComplete,
  refreshTrigger,
}) => {
  const [tasks, setTasks] = useState<BackgroundTask[]>([]);
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState(true);
  const [pollInterval, setPollInterval] = useState<NodeJS.Timeout | null>(null);
  const [lastSeenCompletedTaskIds, setLastSeenCompletedTaskIds] = useState<Set<string>>(new Set());
  const [hasActiveTasks, setHasActiveTasks] = useState(false);

  const fetchTasks = async () => {
    try {
      const token = await getToken();
      if (!token) {
        // Not authenticated, skip fetch
        return;
      }
      const response = await axios.get(
        `${hostname}/lists/${listId}/tasks/active?companyId=${companyId}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      const allTasks = response.data.tasks || [];

      // Check if any tasks just completed (completed and not seen before)
      const completedTasks = allTasks.filter(
        (t: BackgroundTask) => t.status === "completed"
      );

      const newlyCompletedTasks = completedTasks.filter(
        (t: BackgroundTask) => !lastSeenCompletedTaskIds.has(t.id)
      );

      // Add newly completed task IDs to the set (never remove, only add)
      if (newlyCompletedTasks.length > 0) {
        setLastSeenCompletedTaskIds((prevIds) => {
          const newIds = new Set(prevIds);
          newlyCompletedTasks.forEach((t: BackgroundTask) => newIds.add(t.id));
          return newIds;
        });
      }

      // Show all tasks (pending, running, failed, and completed)
      setTasks(allTasks);

      // If tasks newly completed, trigger the list refresh (only once)
      if (newlyCompletedTasks.length > 0 && onTaskComplete) {
        console.log(`Detected ${newlyCompletedTasks.length} newly completed task(s), refreshing list...`);
        onTaskComplete();
      }

      // Check for active tasks (pending or running)
      const activeTasksExist = allTasks.some(
        (t: BackgroundTask) => t.status === "pending" || t.status === "running"
      );

      setHasActiveTasks(activeTasksExist);

      // Stop polling if there are no active tasks (pending or running)
      if (!activeTasksExist && pollInterval) {
        console.log("No active tasks remaining, stopping polling");
        clearInterval(pollInterval);
        setPollInterval(null);
      }
    } catch (error) {
      console.error("Error fetching background tasks:", error);
    }
  };

  const cancelTask = async (taskId: string) => {
    try {
      setLoading(true);
      const token = await getToken();
      await axios.post(
        `${hostname}/tasks/${taskId}/cancel`,
        {},
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      await fetchTasks();
    } catch (error) {
      console.error("Error cancelling task:", error);
    } finally {
      setLoading(false);
    }
  };

  const clearTask = async (taskId: string) => {
    try {
      setLoading(true);
      const token = await getToken();
      await axios.delete(
        `${hostname}/tasks/${taskId}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      await fetchTasks();
    } catch (error) {
      console.error("Error clearing task:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // Reset tracking when list changes
    setLastSeenCompletedTaskIds(new Set());

    // Clear any existing polling interval
    if (pollInterval) {
      clearInterval(pollInterval);
      setPollInterval(null);
    }

    // Fetch tasks once on mount or when listId changes
    fetchTasks();

    return () => {
      if (pollInterval) {
        clearInterval(pollInterval);
      }
    };
  }, [listId]);

  // Effect to refresh tasks when refreshTrigger changes (when a new task is created)
  useEffect(() => {
    if (refreshTrigger && refreshTrigger > 0) {
      fetchTasks();

      // Start polling when a new task is created (only if not already polling)
      if (!pollInterval) {
        console.log("Starting polling due to new task trigger");
        const interval = setInterval(() => {
          fetchTasks();
        }, 5000);
        setPollInterval(interval);
      }
    }
  }, [refreshTrigger]);

  // Effect to start polling when active tasks are detected
  useEffect(() => {
    if (hasActiveTasks && !pollInterval) {
      console.log("Starting polling due to active tasks");
      const interval = setInterval(() => {
        fetchTasks();
      }, 5000);
      setPollInterval(interval);
    }
  }, [hasActiveTasks]);

  const getStatusIcon = (status: string) => {
    switch (status) {
      case "pending":
        return <HourglassEmpty color="action" fontSize="small" />;
      case "running":
        return <PlayArrow color="primary" fontSize="small" />;
      case "completed":
        return <CheckCircle color="success" fontSize="small" />;
      case "failed":
        return <Error color="error" fontSize="small" />;
      case "cancelled":
        return <Cancel color="warning" fontSize="small" />;
      default:
        return null;
    }
  };

  const getTaskTypeLabel = (taskType: string) => {
    switch (taskType) {
      case "import":
        return "CSV Import";
      case "people_search":
        return "People Search";
      case "people_search_custom":
        return "People Search";
      case "list_action":
        return "List Action";
      default:
        return taskType;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case "pending":
        return "default";
      case "running":
        return "primary";
      case "completed":
        return "success";
      case "failed":
        return "error";
      case "cancelled":
        return "warning";
      default:
        return "default";
    }
  };

  if (tasks.length === 0) {
    return null;
  }

  return (
    <Paper
      elevation={3}
      sx={{
        p: 2,
        mb: 2,
        mt: 2,
        position: "relative",
        border: "1px solid",
        borderColor: "primary.main",
      }}
    >
      <Box display="flex" alignItems="center" justifyContent="space-between">
        <Box display="flex" alignItems="center" gap={1}>
          <Typography variant="h6" component="div">
            Background Tasks
          </Typography>
          <Chip
            label={tasks.length}
            size="small"
            color="primary"
            variant="outlined"
          />
        </Box>
        <Box>
          <IconButton size="small" onClick={fetchTasks} disabled={loading}>
            <Refresh />
          </IconButton>
          <IconButton
            size="small"
            onClick={() => setExpanded(!expanded)}
          >
            {expanded ? <ExpandLess /> : <ExpandMore />}
          </IconButton>
        </Box>
      </Box>

      <Collapse in={expanded}>
        <List sx={{ mt: 1 }}>
          {tasks.map((task, index) => (
            <React.Fragment key={task.id}>
              {index > 0 && <Divider />}
              <ListItem sx={{ px: 0 }}>
                <Box sx={{ width: "100%" }}>
                  <Box
                    display="flex"
                    alignItems="center"
                    justifyContent="space-between"
                    mb={1}
                  >
                    <Box display="flex" alignItems="center" gap={1}>
                      {getStatusIcon(task.status)}
                      <Typography variant="subtitle1" fontWeight="medium">
                        {getTaskTypeLabel(task.taskType)}
                      </Typography>
                      <Chip
                        label={task.status}
                        size="small"
                        color={getStatusColor(task.status) as any}
                        variant="outlined"
                      />
                    </Box>
                    {task.status === "running" && (
                      <IconButton
                        size="small"
                        onClick={() => cancelTask(task.id)}
                        disabled={loading}
                        color="error"
                        title="Cancel task"
                      >
                        <Cancel />
                      </IconButton>
                    )}
                    {(task.status === "failed" || task.status === "completed") && (
                      <Button
                        size="small"
                        onClick={() => clearTask(task.id)}
                        disabled={loading}
                        variant="outlined"
                        startIcon={<Cancel />}
                      >
                        Clear
                      </Button>
                    )}
                  </Box>

                  {task.progressMessage && (
                    <Typography variant="body2" color="text.secondary" mb={1}>
                      {task.progressMessage}
                    </Typography>
                  )}

                  {task.errorMessage && (
                    <Alert severity="error" sx={{ mb: 1 }}>
                      {task.errorMessage}
                    </Alert>
                  )}

                  {task.status === "running" && (
                    <Box sx={{ width: "100%", mb: 1 }}>
                      <Box display="flex" alignItems="center" gap={2}>
                        <Box sx={{ flexGrow: 1 }}>
                          <LinearProgress
                            variant="determinate"
                            value={task.progressPercentage || 0}
                            sx={{ height: 8, borderRadius: 4 }}
                          />
                        </Box>
                        <Typography variant="body2" color="text.secondary">
                          {task.progressPercentage?.toFixed(0) || 0}%
                        </Typography>
                      </Box>
                      <Box
                        display="flex"
                        justifyContent="space-between"
                        mt={0.5}
                      >
                        <Typography variant="caption" color="text.secondary">
                          {task.processedItems || 0} / {task.totalItems || 0} items
                        </Typography>
                        {(task.successItems > 0 || task.failedItems > 0) && (
                          <Typography variant="caption" color="text.secondary">
                            ✓ {task.successItems || 0} | ✗ {task.failedItems || 0}
                          </Typography>
                        )}
                      </Box>
                    </Box>
                  )}

                  {task.status === "completed" && (
                    <Box display="flex" flexWrap="wrap" gap={1} mt={1}>
                      {/* Show detailed breakdown if available in result */}
                      {task.result && (task.result.createdCount !== undefined || task.result.imported !== undefined || task.result.totalFound !== undefined) ? (
                        (() => {
                          const newCount = task.result.createdCount ?? task.result.imported ?? 0;
                          const updatedCount = task.result.updatedCount ?? task.result.updated ?? 0;
                          const failedCount = task.failedItems || task.result.failedCount || task.result.failed || 0;
                          const totalCount = task.result.totalRows ?? task.result.totalFound ?? task.totalItems ?? 0;
                          const skippedCount = totalCount - newCount - updatedCount - failedCount;

                          return (
                            <>
                              <Chip
                                size="small"
                                label={`${newCount} new`}
                                color="primary"
                                variant="outlined"
                              />
                              {updatedCount > 0 && (
                                <Chip
                                  size="small"
                                  label={`${updatedCount} updated`}
                                  color="default"
                                  variant="outlined"
                                />
                              )}
                              {skippedCount > 0 && (
                                <Chip
                                  size="small"
                                  label={`${skippedCount} skipped (duplicates)`}
                                  color="warning"
                                  variant="outlined"
                                />
                              )}
                              {failedCount > 0 && (
                                <Chip
                                  size="small"
                                  label={`${failedCount} failed`}
                                  color="error"
                                  variant="outlined"
                                />
                              )}
                              <Typography variant="body2" color="text.secondary" sx={{ ml: 1, alignSelf: "center" }}>
                                ({totalCount} total)
                              </Typography>
                            </>
                          );
                        })()
                      ) : (
                        <>
                          <Chip
                            size="small"
                            label={`${task.successItems || task.processedItems || 0} processed`}
                            color="primary"
                            variant="outlined"
                          />
                          {task.failedItems > 0 && (
                            <Chip
                              size="small"
                              label={`${task.failedItems} failed`}
                              color="error"
                              variant="outlined"
                            />
                          )}
                        </>
                      )}
                    </Box>
                  )}
                </Box>
              </ListItem>
            </React.Fragment>
          ))}
        </List>
      </Collapse>
    </Paper>
  );
};

export default BackgroundTasksPanel;